import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import FullScreenLoader from '../components/FullScreenLoader/FullScreenLoader';
import { getMe } from '../services/opw.api';
import { IContext } from '../types';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';

const AuthContext = React.createContext<IContext>({
  data: { token: '', user: null },
  updateData: (key: string, value: any) => {},
  logout: () => {},
});

const AuthProvider = (props: { children: JSX.Element[] | JSX.Element | null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { inProgress } = useMsal();

  const isPubUrl = location.pathname.slice(0, 7) === '/claim/' && location.pathname.length > 7;
  const isAuthPage = [
    '/login',
    '/login/',
    '/register',
    '/register/',
    '/credentials',
    '/credentials/',
  ].includes(location.pathname);
  const isRegisterPage = ['/register', '/register/', '/credentials', '/credentials/'].includes(
    location.pathname,
  );

  const isMsalProcessing = inProgress !== InteractionStatus.None;

  const [data, setData] = useState(() => {
    const localStorageData = localStorage.getItem('data');
    if (localStorageData) return JSON.parse(localStorageData);
    else return { token: '', user: null };
  });

  const [checked, setChecked] = useState(false);

  const updateData = (key: string, value: any) => {
    setData((prevData: any) => ({ ...prevData, [key]: value }));
  };

  const clearSessionAndRedirect = () => {
    setData({ token: '', user: null });
    localStorage.clear();
    sessionStorage.clear();
    navigate('/login');
  };

  const logout = () => {
    clearSessionAndRedirect();
  };

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const isLoginRequest = error?.config?.url?.includes('/auth/login');

        if ((status === 401 || status === 403) && !isLoginRequest) {
          toast.error('Session Expired Please Login Again', {
            toastId: 'session-expired',
          });
          clearSessionAndRedirect();
        }

        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);

  useEffect(() => {
    // Always persist the current auth state to localStorage, including the
    // cleared state on logout. Previously this only wrote when a token was
    // present, so logging out (which sets token to '') never overwrote the
    // stale stored token in localStorage.
    localStorage.setItem('data', JSON.stringify(data));
  }, [data]);

  useEffect(() => {
    const getUserData = async () => {
      if (!data?.token) {
        setChecked(true);
        return;
      }
      try {
        const request = await getMe(data?.token);
        if (request?.data?.user) {
          updateData('user', request.data.user);
        }
        setChecked(true);
      } catch (err) {
        console.log('error', err);
        setChecked(true);
        setData({ token: '', user: null });
      }
    };

    if (!isPubUrl && !isAuthPage && !isMsalProcessing) getUserData();
  }, [isPubUrl, isAuthPage, isMsalProcessing]);

  if (isMsalProcessing) {
    return <FullScreenLoader />;
  }

  if (location.pathname !== '/login' && !checked && !isPubUrl && !isAuthPage) {
    return <FullScreenLoader />;
  }

  if (isPubUrl || isAuthPage) {
    if (data?.token && isRegisterPage) {
      return <Navigate to={'/'} replace />;
    }
    return (
      <AuthContext.Provider value={{ data, updateData, logout }}>
        {props.children}
      </AuthContext.Provider>
    );
  }

  if (!data?.token) {
    return <Navigate to={'/login'} replace />;
  }

  return (
    <AuthContext.Provider value={{ data, updateData, logout }}>
      {props.children}
    </AuthContext.Provider>
  );
};

export { AuthProvider };

export default AuthContext;
